import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { getCompanyIntegrations, getIntegrationHealth } from '../api';
import { INTEGRATIONS_CATALOG } from '../components/Integrations/mockData';
import RequestDrawer from '../components/Integrations/RequestDrawer';
import { IntegrationLogo } from '../components/Integrations/IntegrationsGrid';
import toast from 'react-hot-toast';
import { Button, SkeletonText, SkeletonBox, TableSkeleton } from '@/components';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import { WhatsAppSetupForm } from '@/modules/Authentication';
import { usePermissions } from '@/common/hooks/usePermissions';
import {
  DashboardPage,
  DashboardCard,
  DashboardTableWrapper,
  DashboardTable,
  DashboardTableHeader,
  DashboardTableBody,
  DashboardTableRow,
} from '../components/PremiumDesign';

import { Navigate } from 'react-router-dom';

function isWhatsAppApp(appId) {
  return appId === 'whatsapp-business' || appId === 'whatsapp-business-prov';
}

export function IntegrationDetailPage() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [requestDrawerOpen, setRequestDrawerOpen] = useState(false);
  const [showReconnectMeta, setShowReconnectMeta] = useState(false);
  const { role } = usePermissions();

  const getCompanyId = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        return userObj?.company_id || localStorage.getItem('company_id');
      }
    } catch (e) {
      console.error('Failed to parse user object from localStorage', e);
    }
    return localStorage.getItem('company_id');
  };

  const companyId = getCompanyId();

  const allApps = [
    ...INTEGRATIONS_CATALOG.messengers,
    ...(INTEGRATIONS_CATALOG.providers || []),
  ];
  const app = allApps.find((a) => a.id === appId);

  const {
    data: companyIntegrations = [],
    isLoading: isIntegrationsApiLoading,
    refetch: refetchIntegrations,
  } = useQuery({
    queryKey: ['company-integrations', companyId],
    queryFn: () => getCompanyIntegrations(companyId),
    enabled:
      !!companyId &&
      appId !== 'instagram' &&
      appId !== 'instagram-business' &&
      appId !== 'google_workspace' &&
      appId !== 'google-workspace' &&
      appId !== 'other_email' &&
      appId !== 'other-email',
  });

  const isIntegrationsLoading = useDelayedSkeleton(isIntegrationsApiLoading);

  const activeIntegration =
    companyIntegrations?.find(
      (item) => item.integration_slug === 'whatsapp' && isWhatsAppApp(appId),
    ) || companyIntegrations?.find((item) => item.integration_slug === appId);

  const companyIntegrationId = activeIntegration?.id;

  const {
    data: healthData,
    isLoading: isHealthApiLoading,
    isFetching: isHealthFetching,
    isError: isHealthError,
    error: healthError,
    refetch: refetchHealth,
  } = useQuery({
    queryKey: ['integration-health', companyId, companyIntegrationId],
    queryFn: () => getIntegrationHealth({ companyId, companyIntegrationId }),
    enabled:
      !!companyId &&
      !!companyIntegrationId &&
      !!app &&
      appId !== 'instagram' &&
      appId !== 'instagram-business' &&
      appId !== 'google_workspace' &&
      appId !== 'google-workspace' &&
      appId !== 'other_email' &&
      appId !== 'other-email',
    placeholderData: keepPreviousData,
  });

  const showDelayedHealthSkeleton = useDelayedSkeleton(
    isHealthFetching && healthData !== undefined,
    300,
    500,
  );
  const isHealthLoading = isHealthApiLoading || showDelayedHealthSkeleton;

  if (appId === 'instagram' || appId === 'instagram-business') {
    return <Navigate to="/integrations/instagram-business" replace />;
  }

  if (appId === 'google_workspace' || appId === 'google-workspace') {
    return <Navigate to="/integrations/google-workspace" replace />;
  }

  if (appId === 'other_email' || appId === 'other-email') {
    return <Navigate to="/integrations/other-email" replace />;
  }

  const healthList = Array.isArray(healthData)
    ? healthData
    : healthData
      ? [healthData]
      : [];

  const isInstalled = !!activeIntegration;
  const isWhatsApp = isWhatsAppApp(appId);

  const handleInstallApp = () => {
    toast('Coming soon!', { icon: '⏳', id: 'coming-soon' });
  };

  const handleWhatsAppConnected = async () => {
    setShowReconnectMeta(false);
    await queryClient.invalidateQueries({
      queryKey: ['company-integrations', companyId],
    });
    await refetchIntegrations();
    if (companyIntegrationId) {
      await queryClient.invalidateQueries({
        queryKey: ['integration-health', companyId, companyIntegrationId],
      });
      refetchHealth();
    }
  };

  if (!app) {
    return (
      <DashboardPage>
        <div className="w-full flex flex-col items-center justify-center min-h-[300px] gap-4 font-sf-pro-display text-center">
          <h2 className="text-xl font-bold text-text-primary mb-2">
            Integration Not Found
          </h2>
          <Button
            variant="primary"
            onClick={() => navigate('/integrations')}
            className="px-6 h-[44px]"
          >
            Back to Catalog
          </Button>
        </div>
      </DashboardPage>
    );
  }

  const resolvedIntegrationName =
    activeIntegration?.integration_name || app.name;
  const resolvedAppForDrawer = { ...app, name: resolvedIntegrationName };

  return (
    <DashboardPage>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/integrations')}
            className="w-8 h-8 rounded-md border border-gray-300 hover:bg-gray-50 text-gray-700 flex items-center justify-center transition-colors focus:outline-none shrink-0 bg-white shadow-sm"
            title="Back to Catalog"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h1 className="text-[22px] font-semibold text-gray-900 tracking-tight">
            {resolvedIntegrationName} Details
          </h1>
        </div>
      </div>

      <div className="w-full">
        {isIntegrationsLoading ? (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
            <div className="xl:col-span-1 flex flex-col gap-6">
              <DashboardCard className="flex flex-col gap-5 p-5 shadow-sm border border-gray-100 rounded-[16px] bg-white">
                <div className="flex items-center gap-3">
                  <SkeletonBox
                    className="h-[52px] w-[52px]"
                    rounded="rounded-full"
                  />
                  <div className="flex flex-col gap-2">
                    <SkeletonText width="w-32" height="h-5" />
                    <SkeletonText width="w-20" height="h-4" />
                  </div>
                </div>
                <div className="flex flex-col gap-4 text-sm mt-2">
                  <SkeletonText width="w-full" height="h-12" />
                  <SkeletonText width="w-full" height="h-12" />
                </div>
              </DashboardCard>
              {/* Second skeleton card to mimic WhatsApp Setup Form */}
              <DashboardCard className="flex flex-col gap-5 p-5 shadow-sm border border-gray-100 rounded-[16px] bg-white">
                <SkeletonText width="w-48" height="h-6" />
                <SkeletonText width="w-full" height="h-4" />
                <SkeletonText width="w-full" height="h-4" />
                <SkeletonText width="w-3/4" height="h-4" />
                <div className="flex gap-2 mt-2">
                  {[...Array(6)].map((_, i) => (
                    <SkeletonBox
                      key={i}
                      className="w-12 h-12"
                      rounded="rounded-lg"
                    />
                  ))}
                </div>
                <SkeletonBox
                  className="w-full sm:w-[200px] h-10 mt-2"
                  rounded="rounded-button"
                />
              </DashboardCard>
            </div>
            <div className="xl:col-span-2 min-w-0">
              <TableSkeleton columns={5} rows={3} />
            </div>
          </div>
        ) : !isInstalled ? (
          <DashboardCard className="max-w-xl mx-auto text-left py-10 px-6 space-y-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <IntegrationLogo type={app.logoType} size="h-16 w-16" />
              <div className="space-y-2">
                <h2 className="text-xl font-bold text-text-primary">
                  {isWhatsApp
                    ? `Connect ${app.name}`
                    : `Install ${app.name} Integration`}
                </h2>
                <p className="text-sm text-text-secondary leading-relaxed">
                  {isWhatsApp
                    ? 'Use Meta Embedded Signup to link your WhatsApp Business account to this workspace.'
                    : 'Connect this messaging channel to sync customer conversations, templates, and metrics directly within your workspace dashboards.'}
                </p>
              </div>
            </div>

            {isWhatsApp ? (
              <WhatsAppSetupForm
                companyId={companyId}
                successHint="Refreshing integration status…"
                onSuccess={handleWhatsAppConnected}
              />
            ) : (
              <div className="pt-2 flex justify-center">
                <Button
                  variant="primary"
                  onClick={handleInstallApp}
                  className="py-3 px-8 h-[48px]"
                >
                  Install Connection
                </Button>
              </div>
            )}
          </DashboardCard>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
            {/* Left Column: Integration Card */}
            <div className="xl:col-span-1 flex flex-col gap-6">
              <DashboardCard className="flex flex-col gap-5 p-5 shadow-sm border border-gray-100 rounded-[16px] bg-white">
                <div className="flex items-center gap-3">
                  <IntegrationLogo
                    type={app.logoType}
                    size="h-[52px] w-[52px]"
                  />
                  <div className="flex flex-col items-start gap-1">
                    <h2 className="text-base font-semibold text-gray-900 leading-tight">
                      {resolvedIntegrationName}
                    </h2>
                    <span className="text-[10px] font-bold text-[#1a73e8] bg-[#e8f0fe] px-2.5 py-0.5 rounded">
                      {activeIntegration?.status_label || 'Account Activated'}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col gap-4 text-sm mt-2">
                  {(activeIntegration?.config?.business_name ||
                    healthData?.connected_workspace_name) && (
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm text-gray-400 font-medium">
                        Business
                      </span>
                      <span className="text-gray-900 text-base">
                        {healthData?.connected_workspace_name ||
                          activeIntegration?.config?.business_name ||
                          'Flexion Automations'}
                      </span>
                    </div>
                  )}
                  {(activeIntegration?.config?.mobile_number ||
                    healthData?.connected_account_name) && (
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm text-gray-400 font-medium">
                        Phone
                      </span>
                      <span className="text-gray-900 text-base">
                        {healthData?.connected_account_name ||
                          activeIntegration?.config?.mobile_number ||
                          '+91 79848 10974'}
                      </span>
                    </div>
                  )}
                </div>

                {isWhatsApp && role === 'owner' && (
                  <button
                    onClick={() => setShowReconnectMeta((v) => !v)}
                    className="mt-2 w-max bg-primary hover:bg-primary/90 text-white text-[13px] font-medium py-2 px-4 rounded-md flex items-center gap-2 transition-colors"
                  >
                    {showReconnectMeta
                      ? 'Cancel Reconnect'
                      : 'Reconnect with Meta'}
                    {!showReconnectMeta && (
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M14 5l7 7m0 0l-7 7m7-7H3"
                        />
                      </svg>
                    )}
                  </button>
                )}
              </DashboardCard>

              {isWhatsApp && showReconnectMeta && role === 'owner' && (
                <DashboardCard className="border border-gray-100 shadow-sm rounded-[16px] bg-white p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">
                    Reconnect WhatsApp with Meta
                  </h3>
                  <WhatsAppSetupForm
                    companyId={companyId}
                    successHint="Refreshing integration status…"
                    onSuccess={handleWhatsAppConnected}
                  />
                </DashboardCard>
              )}
            </div>

            {/* Right Column: Details Table */}
            <div className="xl:col-span-2 min-w-0">
              {isHealthLoading || !healthData ? (
                <TableSkeleton columns={5} rows={10} />
              ) : isHealthError ? (
                <div className="w-full p-8 bg-red-500/5 border border-red-500/10 rounded-2xl flex flex-col items-center justify-center gap-3 text-center">
                  <div className="text-red-500 text-sm font-semibold">
                    Failed to fetch integration health status
                  </div>
                  <p className="text-xs text-text-muted max-w-sm">
                    {healthError?.response?.data?.detail ||
                      healthError?.message ||
                      'Error occurred while contacting the health endpoint.'}
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => refetchHealth()}
                    className="py-2 px-5 h-[38px] text-xs"
                  >
                    Retry Check
                  </Button>
                </div>
              ) : (
                <DashboardTableWrapper>
                  <DashboardTable className="w-full">
                    <DashboardTableHeader>
                      <th className="py-3 px-6 text-left font-medium">
                        Integration Name
                      </th>
                      <th className="py-3 px-6 text-left font-medium">
                        Integration Slug
                      </th>
                      <th className="py-3 px-6 text-center font-medium">
                        Lifecycle Status
                      </th>
                      <th className="py-3 px-6 text-center font-medium">
                        Status
                      </th>
                      <th className="py-3 px-6 text-center font-medium w-24">
                        Action
                      </th>
                    </DashboardTableHeader>
                    <DashboardTableBody>
                      {healthList.length === 0 ? (
                        <tr>
                          <td
                            colSpan="5"
                            className="py-16 text-center text-text-muted border-b border-gray-100"
                          >
                            <span className="text-[13px] font-medium">
                              No integrations found for this application.
                            </span>
                          </td>
                        </tr>
                      ) : (
                        healthList.map((item, idx) => {
                          const lifecycleLower =
                            item.lifecycle_status?.toLowerCase() || '';
                          const isLifecycleActivated =
                            lifecycleLower === 'activated';

                          const healthLower =
                            item.health_status?.toLowerCase() || '';
                          const isHealthy = healthLower === 'healthy';

                          return (
                            <DashboardTableRow
                              key={item.id || idx}
                              className="border-b border-gray-50 last:border-none"
                            >
                              <td className="py-4 px-6 text-[13px] text-gray-900 font-medium">
                                {item.integration_name}
                              </td>
                              <td className="py-4 px-6 text-[13px] text-gray-900">
                                {item.integration_slug}
                              </td>
                              <td className="py-4 px-6 text-center">
                                <span
                                  className={`inline-block px-3 py-1 text-[11px] font-semibold rounded ${
                                    isLifecycleActivated
                                      ? 'text-primary bg-primary/10'
                                      : 'text-amber-600 bg-amber-50'
                                  }`}
                                >
                                  {item.lifecycle_status_label ||
                                    item.lifecycle_status ||
                                    'Account Activated'}
                                </span>
                              </td>
                              <td className="py-4 px-6 text-center">
                                <span
                                  className={`inline-block px-3 py-1 text-[11px] font-semibold rounded ${
                                    isHealthy
                                      ? 'text-primary bg-primary/10'
                                      : 'text-red-500 bg-red-50'
                                  }`}
                                >
                                  {item.health_status_label ||
                                    item.health_status ||
                                    'Unhealthy'}
                                </span>
                              </td>
                              <td
                                className="py-4 px-6 text-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="flex items-center justify-center gap-3">
                                  <button
                                    onClick={() =>
                                      navigate(`/integrations/${appId}/history`)
                                    }
                                    title="View Health History"
                                    className="text-orange-500 hover:opacity-70 transition-opacity"
                                  >
                                    <svg
                                      className="w-[18px] h-[18px]"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                      />
                                    </svg>
                                  </button>
                                  <button
                                    disabled={isHealthy}
                                    onClick={() => setRequestDrawerOpen(true)}
                                    title={
                                      isHealthy
                                        ? 'Integration is Healthy'
                                        : 'View Integration Requests'
                                    }
                                    className={`transition-opacity ${
                                      isHealthy
                                        ? 'text-purple-500 opacity-40 cursor-not-allowed'
                                        : 'text-purple-500 hover:opacity-70'
                                    }`}
                                  >
                                    <svg
                                      className="w-[18px] h-[18px]"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              </td>
                            </DashboardTableRow>
                          );
                        })
                      )}
                    </DashboardTableBody>
                  </DashboardTable>
                </DashboardTableWrapper>
              )}
            </div>
          </div>
        )}
      </div>

      <RequestDrawer
        integration={resolvedAppForDrawer}
        companyIntegrationId={companyIntegrationId}
        isOpen={requestDrawerOpen}
        onClose={() => setRequestDrawerOpen(false)}
      />
    </DashboardPage>
  );
}
